import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffTimetableService, StaffTimetablePeriod } from '../services/staff-timetable.service';
import { StaffSessionService } from '../services/staff-session.service';
import {
  TimelinePeriod,
  calculateTimelineMetrics,
  getTimeTopOffset,
  isPastPeriod,
  isCurrentPeriod,
  nowToMinutes,
  getCurrentWeekDays,
  WeekDay,
} from '../shared/staff-timetable-timeline';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

const HOUR_HEIGHT = 80;

@Component({
  selector: 'app-staff-my-timetable',
  templateUrl: './staff-my-timetable.component.html',
  styleUrls: ['../shared/staff-common.css', '../class-timetable/staff-class-timetable.component.css', './staff-my-timetable.component.css']
})
export class StaffMyTimetableComponent implements OnInit, OnDestroy {
  loading = true;
  loadError = false;

  weekDays: WeekDay[] = getCurrentWeekDays();
  selectedDay: WeekDay = this.weekDays.find(d => d.isToday) || this.weekDays[0];

  timeTableByDay: { [dayName: string]: StaffTimetablePeriod[] } = {};
  periods: TimelinePeriod[] = [];
  hourTops: number[] = [];
  totalHeight = 24 * HOUR_HEIGHT;
  hourHeight = HOUR_HEIGHT;

  now = new Date();
  private nowTimer: any;
  private scrollTimer: any;

  constructor(
    private router: Router,
    private location: Location,
    private staffTimetableService: StaffTimetableService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.nowTimer = setInterval(() => { this.now = new Date(); }, 60000);
    this.loadTimetable();
  }

  ngOnDestroy(): void {
    if (this.nowTimer) { clearInterval(this.nowTimer); }
    if (this.scrollTimer) { clearTimeout(this.scrollTimer); }
  }

  async loadTimetable() {
    const staffDetails = this.staffSession.getStaffDetails();
    if (!staffDetails) { return; }
    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(this.staffTimetableService.getStaffTimetable(staffDetails.staffId));
      if (response && response.status) {
        this.timeTableByDay = response.body?.timeTable || {};
      } else {
        this.loadError = true;
        const unableToLoadYourTimetableTranslation = await firstValueFrom(this.translate.get('unableToLoadYourTimetable'));
        safeToast(unableToLoadYourTimetableTranslation, 'error');
      }
    } catch (e) {
      this.loadError = true;
      const unableToLoadYourTimetableTranslation = await firstValueFrom(this.translate.get('unableToLoadYourTimetable'));
      safeToast(unableToLoadYourTimetableTranslation, 'error');
    } finally {
      this.loading = false;
      this.applySelectedDay();
    }
  }

  selectDay(day: WeekDay) {
    this.selectedDay = day;
    this.applySelectedDay();
  }

  private applySelectedDay() {
    const rawPeriods = this.timeTableByDay[this.selectedDay.dayName] || [];

    const normalized: TimelinePeriod[] = rawPeriods.map(p => ({
      periodId: p.periodId,
      periodStart: p.periodStart,
      periodEnd: p.periodEnd,
      title: p.subjectName,
      subtitle: p.className,
      isBreak: false,
    }));

    const metrics = calculateTimelineMetrics(normalized, this.hourHeight);
    this.periods = metrics.periods;
    this.hourTops = metrics.hourTops;
    this.totalHeight = metrics.totalHeight;

    this.scheduleAutoScroll();
  }

  private scheduleAutoScroll() {
    if (this.scrollTimer) { clearTimeout(this.scrollTimer); }
    this.scrollTimer = setTimeout(() => {
      const container = document.getElementById('smt-timeline-scroll');
      if (!container) { return; }
      let offset: number;
      if (this.selectedDay.isToday) {
        offset = getTimeTopOffset(this.currentTimeString, this.hourHeight);
      } else if (this.periods.length) {
        offset = this.periods[0].top || 0;
      } else {
        return;
      }
      container.scrollTo({ top: Math.max(offset - 100, 0), behavior: 'smooth' });
    }, 600);
  }

  get currentTimeString(): string {
    const h = String(this.now.getHours()).padStart(2, '0');
    const m = String(this.now.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  get currentTimeLabel(): string {
    const hours24 = this.now.getHours();
    const m = String(this.now.getMinutes()).padStart(2, '0');
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    return `${hours12}:${m} ${period}`;
  }

  get nowOffset(): number {
    return getTimeTopOffset(this.currentTimeString, this.hourHeight);
  }

  get showNowLine(): boolean {
    return this.selectedDay.isToday;
  }

  hourLabel(hour: number): string {
    if (hour === 0 || hour === 24) { return '12 AM'; }
    if (hour === 12) { return '12 PM'; }
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  }

  isPast(p: TimelinePeriod): boolean {
    return isPastPeriod(this.selectedDay.isToday, p.periodEnd, nowToMinutes(this.now));
  }

  isCurrent(p: TimelinePeriod): boolean {
    return isCurrentPeriod(this.selectedDay.isToday, p.periodStart, p.periodEnd, nowToMinutes(this.now));
  }

  // Reached from more than one place (sidebar/bottom-nav directly, or the
  // Class Timetable shortcut icon) with no shared destination to hardcode —
  // go back to wherever the user actually came from, same pattern as the
  // student-detail screen's back button.
  goBack() {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.router.navigate(['/staff/classes']);
    }
  }
}
